import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Paper, Typography, TextField, Divider, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Avatar, Grid, Pagination, ButtonGroup, Checkbox, FormControlLabel, Tooltip, IconButton, Badge, Drawer, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TableChartOutlined as TableChartOutlinedIcon, ViewModuleOutlined as ViewModuleOutlinedIcon } from '@mui/icons-material';
import { PersonOutline as PersonOutlineIcon } from '@mui/icons-material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ClearIcon from '@mui/icons-material/Clear';
import { API_BASE_URL } from '../config';
import { getAuthaltToken, AUTHALT_API_BASE } from '../services/authaltService';

// API Configuration - token fetched dynamically from backend
const GUEST_API_BASE = AUTHALT_API_BASE;

const Field = ({ label, value }) => (
  <Box sx={{ pb: 1, mb: 1.25, borderBottom: '1px dashed #e5e7eb' }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.2 }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>{String(value || '-')}</Typography>
  </Box>
);

const Guests = ({ onOpenItinerary }) => {
  const [query, setQuery] = useState('');
  const [guests, setGuests] = useState([]);
  const [apiCounts, setApiCounts] = useState({ arriving: 0, inhouse: 0, departing: 0, upcoming: 0 });
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showAll, setShowAll] = useState(true);
  const [mode, setMode] = useState(() => localStorage.getItem('guestMode') || 'inhouse');
  const [layout, setLayout] = useState(() => localStorage.getItem('guestLayout') || 'pinned');
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem('guestGroupBy') || 'guest');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem('guestPageSize')) || 120);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const s = localStorage.getItem('guestVisibleColumns');
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });
  const getLocalISODate = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  };
  const [anchorDate, setAnchorDate] = useState(() => getLocalISODate());
  
  // Advanced filter panel state
  const [filterPanelOpen, setFilterPanelOpen] = useState(() => localStorage.getItem('guestFilterPanelOpen') === 'true');
  const [advancedFilters, setAdvancedFilters] = useState({
    gender: [],
    guestTag: [],
    contactType: [],
    package: [],
    ageRange: [],
    diet: [],
    duration: [],
    consultingDoctor: []
  });
  const [expandedFilters, setExpandedFilters] = useState({
    gender: true,
    guestTag: true,
    contactType: false,
    package: true,
    ageRange: false,
    diet: false,
    duration: false,
    consultingDoctor: false
  });
  
  const NAME_COL_MIN_WIDTH = 220; // px
  const ID_COL_WIDTH = 140; // px
  const GOLD = '#E4C984';
  const GOLD_BG = 'rgba(228,201,132,0.18)';
  const GOLD_TEXT = '#5b4a1e';
  const APP_BG = '#f6f6f6';
  const BADGE_BG = '#e5e7eb';

  const ViewButton = styled(Button)(({ selected }) => ({
    textTransform: 'none',
    minWidth: '120px',
    backgroundColor: selected ? GOLD : 'transparent',
    color: selected ? '#171717' : '#171717',
    '&:hover': { backgroundColor: selected ? '#cbb16f' : 'rgba(0,0,0,0.04)' },
    borderColor: GOLD,
    fontWeight: 700
  }));

  const GoldButtonGroup = styled(ButtonGroup)(() => ({
    '& .MuiButton-outlined': { borderColor: GOLD },
    '& .MuiButton-outlined:hover': { borderColor: '#cbb16f', backgroundColor: 'rgba(228, 201, 132, 0.12)' },
    '& .MuiButtonBase-root.Mui-focusVisible': { boxShadow: '0 0 0 2px rgba(228, 201, 132, 0.6)' },
    borderRadius: 8
  }));

  const getFirstNonEmpty = (obj, keys) => {
    if (!obj) return '';
    for (const k of keys) {
      const v = obj[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return v;
    }
    return '';
  };

  // Strip HTML tags from text
  const stripHtmlTags = (html) => {
    if (!html) return '';
    const text = String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return text;
  };

  // Truncate text and return both truncated and full versions
  const truncateText = (text, maxLength = 100) => {
    const clean = stripHtmlTags(text);
    if (clean.length <= maxLength) return { short: clean, full: clean, isTruncated: false };
    return { short: clean.substring(0, maxLength) + '...', full: clean, isTruncated: true };
  };

  useEffect(() => {
    localStorage.setItem('guestMode', mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem('guestLayout', layout);
  }, [layout]);

  useEffect(() => {
    localStorage.setItem('guestGroupBy', groupBy);
  }, [groupBy]);

  useEffect(() => {
    localStorage.setItem('guestPageSize', String(pageSize));
  }, [pageSize]);

  useEffect(() => {
    localStorage.setItem('guestFilterPanelOpen', String(filterPanelOpen));
  }, [filterPanelOpen]);

  const toISO = (s) => {
    if (!s) return null;
    const str = String(s).trim();
    // Support YYYY-MM-DD first
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    // Try DD/MM/YYYY or DD-MM-YYYY
    const m = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (m) {
      const d = String(m[1]).padStart(2,'0');
      const mo = String(m[2]).padStart(2,'0');
      const y = String(m[3]).length === 2 ? `20${m[3]}` : String(m[3]);
      return `${y}-${mo}-${d}`;
    }
    // Fallback Date parse
    const t = Date.parse(str);
    if (!Number.isNaN(t)) return new Date(t).toISOString().split('T')[0];
    return null;
  };

  const getCheckInISO = (g) => toISO(getFirstNonEmpty(g, ['checkIn','checkin','startDate','start','arrivalDate']));
  const getCheckOutISO = (g) => toISO(getFirstNonEmpty(g, ['checkOut','checkout','endDate','end','departureDate']));

  const isArriving = (g, d) => {
    const ci = getCheckInISO(g); if (!ci) return false; return ci === d;
  };
  const isDeparting = (g, d) => {
    const co = getCheckOutISO(g); if (!co) return false; return co === d;
  };
  const isInHouse = (g, d) => {
    const ci = getCheckInISO(g); const co = getCheckOutISO(g);
    if (!ci || !co) return false; return ci <= d && d <= co;
  };
  const isUpcoming = (g, d) => {
    const ci = getCheckInISO(g); if (!ci) return false; return ci > d;
  };

  const matchesMode = (g) => {
    // Data is already filtered by the API based on mode, so all guests match
    // This is kept for compatibility with date-based filtering if needed later
    return true;
  };

  const counts = useMemo(() => {
    // Use API-provided counts for display
    return {
      arriving: apiCounts.arriving,
      inhouse: apiCounts.inhouse,
      departing: apiCounts.departing,
      upcoming: apiCounts.upcoming,
      all: apiCounts.arriving + apiCounts.inhouse + apiCounts.departing + apiCounts.upcoming,
    };
  }, [apiCounts]);

  const getStatusLabel = (g) => {
    const d = anchorDate;
    const today = new Date().toISOString().split('T')[0];
    const isToday = d === today;
    
    // For today's date, use API category if available
    if (isToday && g._apiCategory) {
      switch (g._apiCategory) {
        case 'arriving': return 'Arriving';
        case 'inHouse': return 'In-house';
        case 'departing': return 'Departing';
        case 'upcoming': return 'Upcoming';
        default: break;
      }
    }
    
    // Fall back to date-based calculation
    if (isArriving(g, d)) return 'Arriving';
    if (isDeparting(g, d)) return 'Departing';
    if (isInHouse(g, d)) return 'In-house';
    if (isUpcoming(g, d)) return 'Upcoming';
    return '-';
  };

  const formatLocalDateShort = (s) => {
    const iso = toISO(s);
    if (!iso) return '';
    const dt = new Date(iso + 'T00:00:00');
    return dt.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });
  };

  const getStayProgress = (g) => {
    const ci = getCheckInISO(g);
    const co = getCheckOutISO(g);
    if (!ci || !co) return 0;
    const start = new Date(ci + 'T00:00:00');
    const end = new Date(co + 'T00:00:00');
    const now = new Date();
    if (end <= start) return 100;
    const total = end - start;
    const elapsed = Math.min(Math.max(now - start, 0), total);
    return Math.round((elapsed / total) * 100);
  };

  // Helper function to get age range
  const getAgeRange = (age) => {
    if (!age) return null;
    const ageNum = parseInt(age);
    if (isNaN(ageNum)) return null;
    if (ageNum < 20) return '< 20';
    if (ageNum < 30) return '20-30';
    if (ageNum < 40) return '30-40';
    if (ageNum < 50) return '40-50';
    if (ageNum < 60) return '50-60';
    return '60+';
  };

  // Check if guest matches advanced filters
  const matchesAdvancedFilters = useCallback((g) => {
    for (const [key, selectedValues] of Object.entries(advancedFilters)) {
      if (selectedValues.length === 0) continue;
      
      let guestValue;
      if (key === 'ageRange') {
        guestValue = getAgeRange(g.age || g.ageYears);
      } else if (key === 'package') {
        guestValue = String(g.program || g.package || '').trim();
      } else if (key === 'guestTag') {
        guestValue = String(g.tag || g.guestTag || '').trim();
      } else if (key === 'diet') {
        guestValue = String(g.diet || g.dietType || '').trim();
      } else if (key === 'duration') {
        guestValue = String(g.nights || '').trim();
      } else {
        guestValue = String(g[key] || '').trim();
      }
      
      if (!selectedValues.includes(guestValue)) {
        return false;
      }
    }
    return true;
  }, [advancedFilters]);

  // Extract unique filter options from all guests
  const filterOptions = useMemo(() => {
    const extractUnique = (field) => {
      const values = guests
        .map(g => String(g[field] || '').trim())
        .filter(v => v);
      return [...new Set(values)].sort();
    };

    const ages = guests
      .map(g => getAgeRange(g.age || g.ageYears))
      .filter(v => v);
    const uniqueAges = [...new Set(ages)];
    const ageOrder = ['< 20', '20-30', '30-40', '40-50', '50-60', '60+'];
    const sortedAges = uniqueAges.sort((a, b) => ageOrder.indexOf(a) - ageOrder.indexOf(b));

    return {
      gender: extractUnique('gender'),
      guestTag: extractUnique('tag').concat(extractUnique('guestTag')),
      contactType: extractUnique('contactType'),
      package: extractUnique('program').concat(extractUnique('package')),
      ageRange: sortedAges,
      diet: extractUnique('diet').concat(extractUnique('dietType')),
      duration: extractUnique('nights'),
      consultingDoctor: extractUnique('consultingDoctor')
    };
  }, [guests]);

  // Count guests matching each filter option
  const getFilterCounts = useCallback((filterKey, option) => {
    return guests.filter(g => {
      if (filterKey === 'ageRange') {
        return getAgeRange(g.age || g.ageYears) === option;
      } else if (filterKey === 'package') {
        return String(g.program || g.package || '').trim() === option;
      } else if (filterKey === 'guestTag') {
        return String(g.tag || g.guestTag || '').trim() === option;
      } else if (filterKey === 'diet') {
        return String(g.diet || g.dietType || '').trim() === option;
      } else if (filterKey === 'duration') {
        return String(g.nights || '').trim() === option;
      }
      return String(g[filterKey] || '').trim() === option;
    }).length;
  }, [guests]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return Object.values(advancedFilters).reduce((sum, arr) => sum + arr.length, 0);
  }, [advancedFilters]);

  // Clear all advanced filters
  const clearAllFilters = () => {
    setAdvancedFilters({
      gender: [],
      guestTag: [],
      contactType: [],
      package: [],
      ageRange: [],
      diet: [],
      duration: [],
      consultingDoctor: []
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch the authalt token from backend first
        const authToken = await getAuthaltToken();
        if (!authToken) {
          console.error('Failed to get authalt token');
          return;
        }

        // Fetch counts
        const fetchCounts = async () => {
          try {
            const res = await fetch(`${GUEST_API_BASE}/guestlistcountv3`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X_API_KEY': authToken
              },
              body: JSON.stringify({ index: 'atmantan' })
            });
            const data = await res.json();
            if (data) {
              setApiCounts({
                arriving: data.todaysArrival?.count || 0,
                inhouse: data.inHouse?.count || 0,
                departing: data.todaysDeparture?.count || 0,
                upcoming: data.arrivalNext4Days?.count || 0
              });
            }
          } catch (error) {
            console.error('Error fetching counts:', error);
          }
        };

        // Fetch guests
        const fetchGuests = async () => {
          try {
            setLoading(true);
            
            // Map mode to API type parameter
            const typeMap = {
              'arriving': 'TodaysArrival',
              'inhouse': 'InHouse',
              'departing': 'TodaysDeparture',
              'upcoming': 'Next4Days'
            };
            
            // For 'all' mode, we need to fetch all types and combine
            let allGuestsData = [];
            
            if (mode === 'all') {
              const types = ['InHouse', 'TodaysArrival', 'TodaysDeparture', 'Next4Days'];
              for (const type of types) {
                const res = await fetch(`${GUEST_API_BASE}/guestlistdatav3`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X_API_KEY': authToken
                  },
                  body: JSON.stringify({ type, index: 'atmantan' })
                });
                const response = await res.json();
                if (response) {
                  const categoryKey = type === 'InHouse' ? 'inHouse' :
                                     type === 'TodaysArrival' ? 'todaysArrival' :
                                     type === 'TodaysDeparture' ? 'todaysDeparture' : 'arrivalNext4Days';
                  const categoryMap = {
                    'InHouse': 'inHouse',
                    'TodaysArrival': 'arriving',
                    'TodaysDeparture': 'departing',
                    'Next4Days': 'upcoming'
                  };
                  const guestsFromType = (response[categoryKey]?.data || []).map(g => ({
                    ...g,
                    _apiCategory: categoryMap[type]
                  }));
                  allGuestsData = allGuestsData.concat(guestsFromType);
                }
              }
            } else {
              // Fetch specific type based on mode
              const type = typeMap[mode];
              const res = await fetch(`${GUEST_API_BASE}/guestlistdatav3`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X_API_KEY': authToken
                },
                body: JSON.stringify({ type, index: 'atmantan' })
              });
              const response = await res.json();
              if (response) {
                const categoryKey = type === 'InHouse' ? 'inHouse' :
                                   type === 'TodaysArrival' ? 'todaysArrival' :
                                   type === 'TodaysDeparture' ? 'todaysDeparture' : 'arrivalNext4Days';
                const categoryMap = {
                  'InHouse': 'inHouse',
                  'TodaysArrival': 'arriving',
                  'TodaysDeparture': 'departing',
                  'Next4Days': 'upcoming'
                };
                allGuestsData = (response[categoryKey]?.data || []).map(g => ({
                  ...g,
                  _apiCategory: categoryMap[type]
                }));
              }
            }
            
            // Process all guests
            const rows = allGuestsData.map(g => {
              const guestId = g.guestId || g.id || g.GuestId || '';
              const guestName = g.guestName || g.guest_name || ((g.firstName || '') + ' ' + (g.lastName || '')).trim() || g.name || '';
              return { 
                ...g, 
                guestId, 
                guestName,
                // Normalize filter-relevant fields
                gender: g.gender || '',
                tag: g.tag || g.guestTag || '',
                guestTag: g.tag || g.guestTag || '',
                package: g.package || g.program || '',
                program: g.package || g.program || '',
                age: g.age || g.ageYears || '',
                ageYears: g.age || g.ageYears || '',
                contactType: g.contactType || '',
                diet: g.diet || g.dietType || g.dietPreference || '',
                dietType: g.diet || g.dietType || g.dietPreference || '',
                nights: g.nights || g.totalDuration || g.duration || '',
                consultingDoctor: g.consultingDoctor || g.doctor || '',
                _apiCategory: g._apiCategory || '' // Preserve API category
              };
            }).filter(g => {
              // Filter out completely empty/invalid records
              return g.guestId || g.bookingId;
            });
            
            setGuests(rows);
            // Derive columns from keys; put common ones first
            const keys = new Set();
            rows.slice(0, 50).forEach(r => Object.keys(r || {}).forEach(k => keys.add(k)));
            const preferred = ['guestName','guestType','guestId','program','checkIn','checkOut','nights','gender','noOfPax','age','room','phone','consultingDoctor'];
            // Exclude ageYears if age exists; if only ageYears exists, we will still render age (via fallback)
            const HIDE = new Set([
              'chauffeurContactNumber','chauffeurContactNumber2','chauffeurName','chauffeurName2',
              'dropOff','dropoffAddress','dropoffDate','dropoffLocation','dropoffTime',
              'id','name','firstName','lastName','guest_id','guest_name'
            ]);
            const others = Array.from(keys)
              .filter(k => !preferred.includes(k))
              .filter(k => k !== 'ageYears')
              .filter(k => !HIDE.has(k))
              .sort();
            const cols = preferred.filter(k => keys.has(k) || (k === 'age' && keys.has('ageYears'))).concat(others);
            setColumns(cols);
            // initialize visible columns if not set
            setVisibleColumns((prev) => {
              if (!prev) return cols;
              // ensure any new columns are added by default, and guestType is always included if it exists
              const set = new Set(prev);
              // Always add guestType if it exists in columns and isn't already in prev
              if (cols.includes('guestType') && !set.has('guestType')) {
                const guestNameIndex = prev.indexOf('guestName');
                if (guestNameIndex !== -1) {
                  // Insert guestType right after guestName
                  const updated = [...prev];
                  updated.splice(guestNameIndex + 1, 0, 'guestType');
                  return updated;
                }
              }
              const merged = cols.filter(c => set.has(c) || !prev.includes(c));
              return merged;
            });
          } finally {
            setLoading(false);
          }
        };

        await fetchCounts();
        await fetchGuests();
      } catch (error) {
        console.error('Error in fetchData:', error);
      }
    };

    fetchData();
  }, [mode]);

  // persist visible columns
  useEffect(() => {
    if (visibleColumns) {
      try { localStorage.setItem('guestVisibleColumns', JSON.stringify(visibleColumns)); } catch {}
    }
  }, [visibleColumns]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Filter by mode first, then by search query, column filters, and advanced filters
    return guests.filter(g => matchesMode(g)).filter(g => matchesAdvancedFilters(g)).filter(g => {
      const matchesQuery = !q || Object.values(g).some(v => String(v || '').toLowerCase().includes(q));
      if (!matchesQuery) return false;
      for (const [k, v] of Object.entries(filters)) {
        if (!v) continue;
        if (!String(g[k] || '').toLowerCase().includes(String(v).toLowerCase())) return false;
      }
      return true;
    });
  }, [guests, query, filters, mode, anchorDate, matchesAdvancedFilters]);

  // Group guests by booking ID
  const groupedByBooking = useMemo(() => {
    if (groupBy !== 'booking') return null;
    
    const bookingMap = new Map();
    filtered.forEach(guest => {
      const bookingId = guest.bookingId || guest.booking_id || 'NO_BOOKING';
      if (!bookingMap.has(bookingId)) {
        bookingMap.set(bookingId, []);
      }
      bookingMap.get(bookingId).push(guest);
    });
    
    // Sort each booking's guests by guestType (Primary first, then Secondary)
    bookingMap.forEach((guestList, bookingId) => {
      guestList.sort((a, b) => {
        const typeA = (a.guestType || '').toLowerCase();
        const typeB = (b.guestType || '').toLowerCase();
        if (typeA === 'primary') return -1;
        if (typeB === 'primary') return 1;
        return 0;
      });
    });
    
    return Array.from(bookingMap.entries()).map(([bookingId, guestList]) => ({
      bookingId,
      guests: guestList,
      guestCount: guestList.length
    }));
  }, [filtered, groupBy]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / pageSize)), [filtered.length, pageSize]);
  useEffect(() => {
    setPage(1);
  }, [query, filters, mode, anchorDate, guests, pageSize]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(filtered.length, startIndex + pageSize);
  const paged = useMemo(() => filtered.slice(startIndex, endIndex), [filtered, startIndex, endIndex]);
  const columnsToShow = useMemo(() => {
    if (!Array.isArray(columns) || columns.length === 0) return [];
    if (!Array.isArray(visibleColumns) || visibleColumns.length === 0) return columns;
    const set = new Set(visibleColumns);
    return columns.filter(c => set.has(c));
  }, [columns, visibleColumns]);

  const handleClick = (g) => {
    setSelected(g);
    setOpen(true);
  };

  const goItinerary = () => {
    if (!selected) return;
    const payload = {
      guestId: selected.guestId || selected.id,
      guestName: selected.guestName || selected.name,
      guest: selected,
    };
    sessionStorage.setItem('itinerary-pending-guest', JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent('set-itinerary-guest', { detail: payload }));
    if (onOpenItinerary) onOpenItinerary(selected);
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', p: 2, bgcolor: APP_BG }}>
      {/* Advanced Filter Panel */}
      <Drawer
        anchor="left"
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 320,
            p: 2,
            bgcolor: '#fff'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeFilterCount > 0 && (
              <Button
                size="small"
                onClick={clearAllFilters}
                sx={{
                  color: GOLD,
                  '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.12)' }
                }}
              >
                Clear All
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => setFilterPanelOpen(false)}
            >
              <ClearIcon />
            </IconButton>
          </Box>
        </Box>

        {activeFilterCount > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {Object.entries(advancedFilters).map(([key, values]) =>
              values.map(value => (
                <Chip
                  key={`${key}-${value}`}
                  label={value}
                  size="small"
                  onDelete={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      [key]: prev[key].filter(v => v !== value)
                    }));
                  }}
                  sx={{
                    bgcolor: GOLD,
                    color: '#171717',
                    '& .MuiChip-deleteIcon': {
                      color: '#171717',
                      '&:hover': {
                        color: '#000'
                      }
                    }
                  }}
                />
              ))
            )}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Gender Filter */}
        {filterOptions.gender.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                p: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
              onClick={() => setExpandedFilters(prev => ({ ...prev, gender: !prev.gender }))}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Gender
              </Typography>
              {expandedFilters.gender ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedFilters.gender}>
              <Box sx={{ pl: 1 }}>
                {filterOptions.gender.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={advancedFilters.gender.includes(option)}
                        onChange={(e) => {
                          setAdvancedFilters(prev => ({
                            ...prev,
                            gender: e.target.checked
                              ? [...prev.gender, option]
                              : prev.gender.filter(v => v !== option)
                          }));
                        }}
                        sx={{
                          color: GOLD,
                          '&.Mui-checked': { color: GOLD }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {option} ({getFilterCounts('gender', option)})
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Guest Tag Filter */}
        <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                p: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
              onClick={() => setExpandedFilters(prev => ({ ...prev, guestTag: !prev.guestTag }))}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Guest Tag
              </Typography>
              {expandedFilters.guestTag ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedFilters.guestTag}>
              <Box sx={{ pl: 1 }}>
                {filterOptions.guestTag.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={advancedFilters.guestTag.includes(option)}
                        onChange={(e) => {
                          setAdvancedFilters(prev => ({
                            ...prev,
                            guestTag: e.target.checked
                              ? [...prev.guestTag, option]
                              : prev.guestTag.filter(v => v !== option)
                          }));
                        }}
                        sx={{
                          color: GOLD,
                          '&.Mui-checked': { color: GOLD }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {option} ({getFilterCounts('guestTag', option)})
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Collapse>
          </Box>

        {/* Package Filter */}
        {filterOptions.package.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                p: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
              onClick={() => setExpandedFilters(prev => ({ ...prev, package: !prev.package }))}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Package
              </Typography>
              {expandedFilters.package ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedFilters.package}>
              <Box sx={{ pl: 1 }}>
                {filterOptions.package.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={advancedFilters.package.includes(option)}
                        onChange={(e) => {
                          setAdvancedFilters(prev => ({
                            ...prev,
                            package: e.target.checked
                              ? [...prev.package, option]
                              : prev.package.filter(v => v !== option)
                          }));
                        }}
                        sx={{
                          color: GOLD,
                          '&.Mui-checked': { color: GOLD }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {option} ({getFilterCounts('package', option)})
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Age Range Filter */}
        {filterOptions.ageRange.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                p: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
              onClick={() => setExpandedFilters(prev => ({ ...prev, ageRange: !prev.ageRange }))}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Age Range
              </Typography>
              {expandedFilters.ageRange ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedFilters.ageRange}>
              <Box sx={{ pl: 1 }}>
                {filterOptions.ageRange.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={advancedFilters.ageRange.includes(option)}
                        onChange={(e) => {
                          setAdvancedFilters(prev => ({
                            ...prev,
                            ageRange: e.target.checked
                              ? [...prev.ageRange, option]
                              : prev.ageRange.filter(v => v !== option)
                          }));
                        }}
                        sx={{
                          color: GOLD,
                          '&.Mui-checked': { color: GOLD }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {option} ({getFilterCounts('ageRange', option)})
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Contact Type Filter */}
        {filterOptions.contactType.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                p: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
              onClick={() => setExpandedFilters(prev => ({ ...prev, contactType: !prev.contactType }))}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Contact Type
              </Typography>
              {expandedFilters.contactType ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedFilters.contactType}>
              <Box sx={{ pl: 1 }}>
                {filterOptions.contactType.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={advancedFilters.contactType.includes(option)}
                        onChange={(e) => {
                          setAdvancedFilters(prev => ({
                            ...prev,
                            contactType: e.target.checked
                              ? [...prev.contactType, option]
                              : prev.contactType.filter(v => v !== option)
                          }));
                        }}
                        sx={{
                          color: GOLD,
                          '&.Mui-checked': { color: GOLD }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {option} ({getFilterCounts('contactType', option)})
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Diet Type Filter */}
        <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                p: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
              onClick={() => setExpandedFilters(prev => ({ ...prev, diet: !prev.diet }))}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Diet Type
              </Typography>
              {expandedFilters.diet ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedFilters.diet}>
              <Box sx={{ pl: 1 }}>
                {filterOptions.diet.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={advancedFilters.diet.includes(option)}
                        onChange={(e) => {
                          setAdvancedFilters(prev => ({
                            ...prev,
                            diet: e.target.checked
                              ? [...prev.diet, option]
                              : prev.diet.filter(v => v !== option)
                          }));
                        }}
                        sx={{
                          color: GOLD,
                          '&.Mui-checked': { color: GOLD }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {option} ({getFilterCounts('diet', option)})
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Collapse>
          </Box>

        {/* Duration Filter */}
        {filterOptions.duration.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                p: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
              onClick={() => setExpandedFilters(prev => ({ ...prev, duration: !prev.duration }))}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Duration
              </Typography>
              {expandedFilters.duration ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedFilters.duration}>
              <Box sx={{ pl: 1 }}>
                {filterOptions.duration.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={advancedFilters.duration.includes(option)}
                        onChange={(e) => {
                          setAdvancedFilters(prev => ({
                            ...prev,
                            duration: e.target.checked
                              ? [...prev.duration, option]
                              : prev.duration.filter(v => v !== option)
                          }));
                        }}
                        sx={{
                          color: GOLD,
                          '&.Mui-checked': { color: GOLD }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {option}N ({getFilterCounts('duration', option)})
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Consulting Doctor Filter */}
        {filterOptions.consultingDoctor.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                p: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
              onClick={() => setExpandedFilters(prev => ({ ...prev, consultingDoctor: !prev.consultingDoctor }))}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Consulting Doctor
              </Typography>
              {expandedFilters.consultingDoctor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedFilters.consultingDoctor}>
              <Box sx={{ pl: 1 }}>
                {filterOptions.consultingDoctor.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={advancedFilters.consultingDoctor.includes(option)}
                        onChange={(e) => {
                          setAdvancedFilters(prev => ({
                            ...prev,
                            consultingDoctor: e.target.checked
                              ? [...prev.consultingDoctor, option]
                              : prev.consultingDoctor.filter(v => v !== option)
                          }));
                        }}
                        sx={{
                          color: GOLD,
                          '&.Mui-checked': { color: GOLD }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {option} ({getFilterCounts('consultingDoctor', option)})
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </Drawer>

      <Paper sx={{ flex: 1, p: 2, overflow: 'auto', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px', border: '1px solid #e5e7eb', bgcolor: '#fff' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2, mb: 2 }}>Guests</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          {/* Left: Filter Button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={
              <Badge badgeContent={activeFilterCount} color="error">
                <FilterAltOutlinedIcon fontSize="small" />
              </Badge>
            }
            onClick={() => setFilterPanelOpen(!filterPanelOpen)}
            sx={{
              borderColor: GOLD,
              color: filterPanelOpen ? '#171717' : GOLD,
              bgcolor: filterPanelOpen ? GOLD : 'transparent',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: GOLD,
                bgcolor: filterPanelOpen ? '#d4b974' : 'rgba(228, 201, 132, 0.12)'
              }
            }}
          >
            Filter
          </Button>
          
          {loading && <Chip label="Loading" size="small" />}
          
          {/* Right: All other controls */}
          <GoldButtonGroup variant="outlined" size="small" sx={{ ml: 'auto' }}>
            <ViewButton selected={mode === 'arriving'} onClick={() => setMode('arriving')}>Arriving <span style={{ marginLeft: 8, background: BADGE_BG, borderRadius: 999, padding: '0 6px', fontSize: 12 }}>{counts.arriving}</span></ViewButton>
            <ViewButton selected={mode === 'inhouse'} onClick={() => setMode('inhouse')}>In-house <span style={{ marginLeft: 8, background: BADGE_BG, borderRadius: 999, padding: '0 6px', fontSize: 12 }}>{counts.inhouse}</span></ViewButton>
            <ViewButton selected={mode === 'departing'} onClick={() => setMode('departing')}>Departing <span style={{ marginLeft: 8, background: BADGE_BG, borderRadius: 999, padding: '0 6px', fontSize: 12 }}>{counts.departing}</span></ViewButton>
            <ViewButton selected={mode === 'upcoming'} onClick={() => setMode('upcoming')}>Upcoming <span style={{ marginLeft: 8, background: BADGE_BG, borderRadius: 999, padding: '0 6px', fontSize: 12 }}>{counts.upcoming}</span></ViewButton>
            <ViewButton selected={mode === 'all'} onClick={() => setMode('all')}>All <span style={{ marginLeft: 8, background: BADGE_BG, borderRadius: 999, padding: '0 6px', fontSize: 12 }}>{counts.all}</span></ViewButton>
          </GoldButtonGroup>
          <GoldButtonGroup variant="outlined" size="small">
            <ViewButton selected={layout === 'pinned'} onClick={() => setLayout('pinned')} startIcon={<TableChartOutlinedIcon fontSize="small" />}>Table</ViewButton>
            <ViewButton selected={layout === 'cards'} onClick={() => setLayout('cards')} startIcon={<ViewModuleOutlinedIcon fontSize="small" />}>Cards</ViewButton>
          </GoldButtonGroup>
          <GoldButtonGroup variant="outlined" size="small">
            <ViewButton selected={groupBy === 'guest'} onClick={() => setGroupBy('guest')}>By Guest</ViewButton>
            <ViewButton selected={groupBy === 'booking'} onClick={() => setGroupBy('booking')}>By Booking</ViewButton>
          </GoldButtonGroup>
          
          <IconButton
            size="small"
            onClick={() => setColumnsDialogOpen(true)}
            sx={{
              border: `1px solid ${GOLD}`,
              color: GOLD,
              '&:hover': {
                bgcolor: 'rgba(228, 201, 132, 0.12)'
              }
            }}
          >
            <SettingsOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        {layout === 'pinned' && (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  {columns.map(col => (
                    columnsToShow.includes(col) && (
                    <th
                      key={col}
                      className={"px-3 py-2 text-left font-semibold text-gray-800 sticky top-0 text-xs uppercase tracking-wider"}
                      style={{
                        ...(col === 'program' ? { minWidth: 220 } : {}),
                        ...(col === 'checkIn' || col === 'checkOut' ? { minWidth: 120, width: 120 } : {}),
                        ...(col === 'availabilityStatus' ? { minWidth: 100, width: 100 } : {}),
                        ...(col === 'phone' || col === 'phoneNumber' || col === 'mobile' ? { minWidth: 140, width: 140 } : {}),
                        ...(col === 'roomType' ? { minWidth: 180, width: 180 } : {}),
                        ...(col === 'generalInstructions' || col === 'generalComments' ? { minWidth: 400, width: 400 } : {}),
                        background: col === 'guestName' ? '#fff' : '#f3f4f6',
                        borderBottom: '1px solid #e5e7eb',
                        ...(col === 'guestName' ? { position: 'sticky', left: 0, zIndex: 20, minWidth: NAME_COL_MIN_WIDTH, color: '#171717' } : {}),
                        ...(col === 'guestId' ? { position: 'sticky', left: NAME_COL_MIN_WIDTH, zIndex: 19, minWidth: ID_COL_WIDTH, boxShadow: '2px 0 6px -4px rgba(0,0,0,0.25)' } : {})
                      }}
                    >
                      {col}
                    </th>
                    )
                  ))}
                </tr>
                <tr className="bg-white border-b">
                  {columns.map(col => (
                    columnsToShow.includes(col) && (
                    <th
                      key={col}
                      className="px-3 py-1"
                      style={{
                        ...(col === 'program' ? { minWidth: 220 } : {}),
                        ...(col === 'checkIn' || col === 'checkOut' || col === 'startDate' || col === 'endDate' ? { minWidth: 140, width: 140 } : {}),
                        ...(col === 'availabilityStatus' ? { minWidth: 100, width: 100 } : {}),
                        ...(col === 'phone' || col === 'phoneNumber' || col === 'mobile' ? { minWidth: 140, width: 140 } : {}),
                        ...(col === 'roomType' ? { minWidth: 180, width: 180 } : {}),
                        ...(col === 'guestName' ? { position: 'sticky', left: 0, zIndex: 15, background: 'white', minWidth: NAME_COL_MIN_WIDTH } : {}),
                        ...(col === 'guestId' ? { position: 'sticky', left: NAME_COL_MIN_WIDTH, zIndex: 14, background: 'white', minWidth: ID_COL_WIDTH, boxShadow: '2px 0 6px -6px rgba(0,0,0,0.25)' } : {})
                      }}
                    >
                      <input
                        className="w-full border rounded px-2 py-1 text-xs"
                        placeholder="Filter"
                        value={filters[col] || ''}
                        onChange={(e) => setFilters(f => ({ ...f, [col]: e.target.value }))}
                      />
                    </th>
                    )
                  ))}
                </tr>
              </thead>
              <tbody>
              {groupBy === 'guest' ? (
                // Individual guest view
                <>
                {paged.map((g, idx) => {
                  const rowBgColor = idx % 2 === 0 ? '#fff' : 'rgba(249, 250, 251, 1)';
                  return (
                  <tr key={g.guestId || g.id || g.guestName || idx} className="border-b hover:bg-gray-50 cursor-pointer" style={{ backgroundColor: rowBgColor }} onClick={() => handleClick(g)}>
                    {columns.map(col => {
                      const rawValue = g[col];
                      const textFields = ['generalInstructions', 'generalComments', 'comments', 'notes', 'instructions'];
                      const isTextField = textFields.includes(col);
                      const truncated = isTextField ? truncateText(rawValue, 100) : null;
                      
                      return columnsToShow.includes(col) && (
                      <td
                        key={col}
                        className="px-3 py-2"
                        style={{
                          ...(col === 'guestName' ? { position: 'sticky', left: 0, zIndex: 6, minWidth: NAME_COL_MIN_WIDTH, backgroundColor: rowBgColor } : {}),
                          ...(col === 'guestId' ? { position: 'sticky', left: NAME_COL_MIN_WIDTH, zIndex: 5, minWidth: ID_COL_WIDTH, boxShadow: '2px 0 6px -6px rgba(0,0,0,0.25)', backgroundColor: rowBgColor } : {}),
                          ...(col === 'checkIn' || col === 'checkOut' ? { minWidth: 140, width: 140, whiteSpace: 'nowrap' } : {}),
                          ...(col === 'availabilityStatus' ? { minWidth: 100, width: 100 } : {}),
                          ...(col === 'phone' || col === 'phoneNumber' || col === 'mobile' ? { minWidth: 140, width: 140 } : {}),
                          ...(col === 'roomType' ? { minWidth: 180, width: 180 } : {}),
                          ...(col === 'generalInstructions' || col === 'generalComments' ? { 
                            minWidth: 400, 
                            width: 400, 
                            maxWidth: 400,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: '1.4em',
                            maxHeight: '2.8em'
                          } : {})
                        }}
                      >
                        {col === 'guestName' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: GOLD, color: '#fff' }}><PersonOutlineIcon fontSize="small" /></Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} title={String(g[col] || '')}>{String(g[col] || '')}</Typography>
                            </Box>
                          </Box>
                        ) : col === 'guestType' ? (
                          (() => {
                            const v = String(g[col] || '').trim();
                            if (!v) return null;
                            const isPrimary = v.toLowerCase() === 'primary';
                            return (
                              <Chip 
                                label={v} 
                                size="small" 
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.7rem', 
                                  bgcolor: isPrimary ? GOLD_BG : '#e0e0e0', 
                                  color: isPrimary ? GOLD_TEXT : '#666', 
                                  fontWeight: 600,
                                  border: isPrimary ? `1px solid ${GOLD}` : '1px solid #ccc'
                                }} 
                              />
                            );
                          })()
                        ) : col === 'program' ? (
                          (() => {
                            const v = String(g[col] || '').trim();
                            return v ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs whitespace-nowrap" style={{ background: GOLD_BG, color: GOLD_TEXT, border: `1px solid ${GOLD}` }}>
                                {v}
                              </span>
                            ) : null;
                          })()
                        ) : col === 'age' ? (
                          String(g['age'] || g['ageYears'] || '')
                        ) : isTextField && truncated ? (
                          truncated.isTruncated ? (
                            <Tooltip title={truncated.full} arrow placement="top">
                              <span style={{ cursor: 'help' }}>{truncated.short}</span>
                            </Tooltip>
                          ) : (
                            <span>{truncated.short}</span>
                          )
                        ) : (
                          String(g[col] || '')
                        )}
                      </td>
                      );
                    })}
                  </tr>
                  );
                })}
                </>
              ) : (
                // Grouped by booking view
                <>
                {groupedByBooking && groupedByBooking.map((booking, bookingIdx) => (
                  <React.Fragment key={booking.bookingId}>
                    {booking.guests.map((g, guestIdx) => {
                      const isFirstInGroup = guestIdx === 0;
                      const isLastInGroup = guestIdx === booking.guests.length - 1;
                      const rowBgColor = bookingIdx % 2 === 0 ? '#fff' : 'rgba(249, 250, 251, 1)';
                      const guestType = (g.guestType || '').trim();
                      
                      return (
                      <tr 
                        key={g.guestId || g.id || `${booking.bookingId}-${guestIdx}`} 
                        className="hover:bg-gray-50 cursor-pointer" 
                        style={{ 
                          backgroundColor: rowBgColor,
                          borderLeft: `4px solid ${GOLD}`,
                          borderBottom: isLastInGroup ? `2px solid ${GOLD}` : '1px solid #e5e7eb',
                          borderTop: isFirstInGroup ? `2px solid ${GOLD}` : 'none'
                        }} 
                        onClick={() => handleClick(g)}
                      >
                        {columns.map(col => {
                          const rawValue = g[col];
                          const textFields = ['generalInstructions', 'generalComments', 'comments', 'notes', 'instructions'];
                          const isTextField = textFields.includes(col);
                          const truncated = isTextField ? truncateText(rawValue, 100) : null;
                          
                          return columnsToShow.includes(col) && (
                          <td
                            key={col}
                            className="px-3 py-2"
                            style={{
                              ...(col === 'guestName' ? { position: 'sticky', left: 0, zIndex: 6, minWidth: NAME_COL_MIN_WIDTH, backgroundColor: rowBgColor } : {}),
                              ...(col === 'guestId' ? { position: 'sticky', left: NAME_COL_MIN_WIDTH, zIndex: 5, minWidth: ID_COL_WIDTH, boxShadow: '2px 0 6px -6px rgba(0,0,0,0.25)', backgroundColor: rowBgColor } : {}),
                              ...(col === 'checkIn' || col === 'checkOut' || col === 'startDate' || col === 'endDate' ? { minWidth: 140, width: 140, whiteSpace: 'nowrap' } : {}),
                              ...(col === 'availabilityStatus' ? { minWidth: 100, width: 100 } : {}),
                              ...(col === 'phone' || col === 'phoneNumber' || col === 'mobile' ? { minWidth: 140, width: 140 } : {}),
                              ...(col === 'roomType' ? { minWidth: 180, width: 180 } : {}),
                              ...(col === 'generalInstructions' || col === 'generalComments' ? { 
                                minWidth: 400, 
                                width: 400, 
                                maxWidth: 400,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                lineHeight: '1.4em',
                                maxHeight: '2.8em'
                              } : {})
                            }}
                          >
                            {col === 'guestName' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: GOLD, color: '#fff' }}><PersonOutlineIcon fontSize="small" /></Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} title={String(g[col] || '')}>{String(g[col] || '')}</Typography>
                                </Box>
                              </Box>
                            ) : col === 'guestType' ? (
                              (() => {
                                const v = String(g[col] || '').trim();
                                if (!v) return null;
                                const isPrimary = v.toLowerCase() === 'primary';
                                return (
                                  <Chip 
                                    label={v} 
                                    size="small" 
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '0.7rem', 
                                      bgcolor: isPrimary ? GOLD_BG : '#e0e0e0', 
                                      color: isPrimary ? GOLD_TEXT : '#666', 
                                      fontWeight: 600,
                                      border: isPrimary ? `1px solid ${GOLD}` : '1px solid #ccc'
                                    }} 
                                  />
                                );
                              })()
                            ) : col === 'bookingId' && isFirstInGroup ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{booking.bookingId}</Typography>
                                <Chip label={`${booking.guestCount} ${booking.guestCount === 1 ? 'guest' : 'guests'}`} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                              </Box>
                            ) : col === 'bookingId' && !isFirstInGroup ? (
                              <span style={{ color: '#999', fontSize: '0.75rem' }}>↑ Same</span>
                            ) : col === 'program' ? (
                              (() => {
                                const v = String(g[col] || '').trim();
                                return v ? (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs whitespace-nowrap" style={{ background: GOLD_BG, color: GOLD_TEXT, border: `1px solid ${GOLD}` }}>
                                    {v}
                                  </span>
                                ) : null;
                              })()
                            ) : col === 'age' ? (
                              String(g['age'] || g['ageYears'] || '')
                            ) : isTextField && truncated ? (
                              truncated.isTruncated ? (
                                <Tooltip title={truncated.full} arrow placement="top">
                                  <span style={{ cursor: 'help' }}>{truncated.short}</span>
                                </Tooltip>
                              ) : (
                                <span>{truncated.short}</span>
                              )
                            ) : (
                              String(g[col] || '')
                            )}
                          </td>
                          );
                        })}
                      </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
                </>
              )}
                {filtered.length === 0 && (
                  <tr><td className="px-3 py-4 text-gray-500" colSpan={columnsToShow.length}>No guests</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {layout === 'cards' && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
                {paged.map((g, idx) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={g.guestId || g.id || g.guestName || idx}>
                  <Paper onClick={() => handleClick(g)} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25, cursor: 'pointer', backgroundColor: '#fff', borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.06)', transition: 'all 160ms ease', '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.10)', transform: 'translateY(-2px)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar 
                        sx={{ width: 40, height: 40, bgcolor: GOLD }} 
                        src={g.profilePicture || g.photoUrl || ''}
                        alt={g.guestName}
                      >
                        {String(g.guestName || '').slice(0,1).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.guestName || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">ID: {g.guestId || g.id || '-'}</Typography>
                      </Box>
                      <Chip label={getStatusLabel(g)} size="small" sx={{ bgcolor: GOLD_BG, border: `1px solid ${GOLD}`, color: GOLD_TEXT, fontWeight: 600 }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {String(g.program || '').trim() && (
                        <Chip label={String(g.program).trim()} size="small" sx={{ borderColor: GOLD, color: GOLD_TEXT, bgcolor: GOLD_BG }} variant="outlined" />
                      )}
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Check-in</Typography>
                        <Typography variant="body2">{formatLocalDateShort(getFirstNonEmpty(g, ['checkIn','checkin','startDate','start','arrivalDate'])) || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Check-out</Typography>
                        <Typography variant="body2">{formatLocalDateShort(getFirstNonEmpty(g, ['checkOut','checkout','endDate','end','departureDate'])) || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Consulting Doctor</Typography>
                        <Typography variant="body2">{getFirstNonEmpty(g, ['consultingDoctor','doctor','consultant','physician']) || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Room</Typography>
                        <Typography variant="body2">{getFirstNonEmpty(g, ['room','roomNo','roomNumber','cabinNo','cabin','cabin_no']) || '-'}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}
              {filtered.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">No guests</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
        {/* Sticky bottom pagination bar */}
        <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, bgcolor: '#fff', borderTop: '1px solid #e5e7eb', boxShadow: '0 -8px 16px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1, px: 2, zIndex: 25, ml: -2, mr: -2 }}>
          <Typography variant="caption" color="text.secondary">Showing {filtered.length ? `${startIndex + 1}–${endIndex}` : '0'} of {filtered.length}</Typography>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} size="small" />
          <GoldButtonGroup variant="outlined" size="small">
            <ViewButton selected={pageSize === 120} onClick={() => setPageSize(120)}>120</ViewButton>
            <ViewButton selected={pageSize === 150} onClick={() => setPageSize(150)}>150</ViewButton>
          </GoldButtonGroup>
        </Box>
      </Paper>

      {/* Column selection dialog */}
      <Dialog open={columnsDialogOpen} onClose={() => setColumnsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Columns</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button size="small" onClick={() => setVisibleColumns(columns.slice())}>Select All</Button>
              <Button size="small" onClick={() => setVisibleColumns([])}>Clear</Button>
              <Button size="small" onClick={() => { try { localStorage.removeItem('guestVisibleColumns'); } catch {} setVisibleColumns(columns.slice()); }}>Reset</Button>
            </Box>
            {columns.map(col => (
              <FormControlLabel key={col} control={<Checkbox checked={Array.isArray(visibleColumns) ? visibleColumns.includes(col) : true} onChange={(e) => {
                const checked = e.target.checked;
                setVisibleColumns(prev => {
                  const base = Array.isArray(prev) ? prev.slice() : columns.slice();
                  const set = new Set(base);
                  if (checked) set.add(col); else set.delete(col);
                  return Array.from(set);
                });
              }} />} label={col} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Guest Details</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' }, gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Avatar 
                  sx={{ width: 180, height: 180, bgcolor: GOLD }} 
                  src={selected.profilePicture || selected.photoUrl || ''}
                  alt={selected.guestName || selected.name}
                >
                  {String(selected.guestName || selected.name || '?').slice(0,1).toUpperCase()}
                </Avatar>
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{selected.guestName || selected.name}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.25 }}>
                    {getFirstNonEmpty(selected, ['room','roomNo','roomNumber']) ? `Room ${getFirstNonEmpty(selected, ['room','roomNo','roomNumber'])}` : '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.25 }}>{getFirstNonEmpty(selected, ['program','package','plan']) || '-'}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{getFirstNonEmpty(selected, ['nights','totalDuration','duration']) ? `${getFirstNonEmpty(selected, ['nights','totalDuration','duration'])} Nights` : '-'}</Typography>
                </Box>
              </Box>
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Field label="Guest Name" value={selected.guestName || selected.name} />
                    <Field label="Contact Type" value={getFirstNonEmpty(selected, ['contactType','contact_type'])} />
                    <Field label="Guest Status" value={getFirstNonEmpty(selected, ['status','guestStatus'])} />
                    <Field label="Health Form Status" value={getFirstNonEmpty(selected, ['healthFormStatus','health_form_status'])} />
                    <Field label="Package" value={getFirstNonEmpty(selected, ['program','package','plan'])} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field label="Guest Tag" value={getFirstNonEmpty(selected, ['tag','guestTag','tags'])} />
                    <Field label="Room No" value={getFirstNonEmpty(selected, ['room','roomNo','roomNumber'])} />
                    <Field label="Room Type Booked" value={getFirstNonEmpty(selected, ['roomType','room_type'])} />
                    <Field label="Total duration" value={getFirstNonEmpty(selected, ['nights','totalDuration','duration'])} />
                    <Field label="Email ID" value={getFirstNonEmpty(selected, ['email','mail','emailId'])} />
                    <Field label="Booked By" value={getFirstNonEmpty(selected, ['bookedBy','booked_by'])} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field label="Checkin Date" value={getFirstNonEmpty(selected, ['checkIn','checkin','startDate'])} />
                    <Field label="Checkout Date" value={getFirstNonEmpty(selected, ['checkOut','checkout','endDate'])} />
                    <Field label="ETA" value={getFirstNonEmpty(selected, ['eta','ETA'])} />
                    <Field label="ETD" value={getFirstNonEmpty(selected, ['etd','ETD'])} />
                    <Field label="Payment Status" value={getFirstNonEmpty(selected, ['paymentStatus','payment_status'])} />
                    <Field label="Consulting Doctor" value={getFirstNonEmpty(selected, ['consultingDoctor','doctor'])} />
                    <Field label="Age" value={getFirstNonEmpty(selected, ['age','ageYears'])} />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
          {selected && getFirstNonEmpty(selected, ['generalInstructions', 'generalComments', 'instructions']) && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f9fafb', borderRadius: 1, border: '1px solid #e5e7eb' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#171717' }}>General Instructions</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {stripHtmlTags(getFirstNonEmpty(selected, ['generalInstructions', 'generalComments', 'instructions']))}
              </Typography>
            </Box>
          )}
          {selected && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">All Fields</Typography>
                <Button size="small" onClick={() => setShowAll(v => !v)}>{showAll ? 'Hide' : 'Show'}</Button>
              </Box>
              {showAll && (
                <Box sx={{ maxHeight: 260, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                  <table className="min-w-full text-sm">
                    <tbody>
                      {Object.entries(selected).map(([k, v]) => {
                        const textFields = ['generalInstructions', 'generalComments', 'comments', 'notes', 'instructions'];
                        const isTextField = textFields.includes(k);
                        const displayValue = isTextField 
                          ? stripHtmlTags(v)
                          : (Array.isArray(v) ? v.join(', ') : (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? '')));
                        
                        return (
                        <tr key={k} className="border-b">
                          <td className="px-3 py-2 text-gray-600 align-top font-semibold" style={{ width: 220 }}>{k}</td>
                          <td className="px-3 py-2 align-top" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {displayValue}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button onClick={goItinerary} variant="contained" sx={{ bgcolor: '#E4C984', color: '#171717', '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.9)' } }}>Go to Itinerary</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Guests;
